import type { MetadataItem } from "@/ui/metadataList";
import type {
  ComponentBaseContext,
  ComponentBaseMapper,
  ExecutionDetailsContext,
  OutputPayload,
  SubtitleContext,
} from "../types";
import { baseMapper } from "./base";
import { isMetadataItem, MAX_NODE_METADATA_ITEMS, metadataItem } from "./image_common";

interface CreateComputeInstanceConfiguration {
  compartment?: string;
  availabilityDomain?: string;
  displayName?: string;
  shape?: string;
  imageOs?: string;
  image?: string;
  subnet?: string;
  sshPublicKey?: string;
  ocpus?: number;
  memoryInGBs?: number;
  bootVolumeSizeGB?: number;
  bootVolumeVpusPerGB?: string;
  attachBlockVolume?: boolean;
  blockVolume?: string;
  enableShieldedInstance?: boolean;
  enableConfidentialComputing?: boolean;
}

interface CreateComputeInstanceNodeMetadata {
  displayName?: string;
  shape?: string;
  availabilityDomain?: string;
  imageName?: string;
  subnetName?: string;
  blockVolumeName?: string;
}

interface CreateComputeInstanceOutputData {
  instanceId?: string;
  displayName?: string;
  lifecycleState?: string;
  shape?: string;
  availabilityDomain?: string;
  compartmentId?: string;
  region?: string;
  timeCreated?: string;
  publicIp?: string;
  privateIp?: string;
}

type CreateComputeInstanceOutputPayload = OutputPayload & {
  data?: CreateComputeInstanceOutputData;
};

interface CreateComputeInstanceExecutionMetadata {
  startedAt?: string;
}

function getExecutedAt(context: ExecutionDetailsContext): string | undefined {
  const metadata = context.execution.metadata as CreateComputeInstanceExecutionMetadata | undefined;
  const ts = metadata?.startedAt ?? context.execution.createdAt;
  return ts ? new Date(ts).toLocaleString() : undefined;
}

function getOutputData(context: ExecutionDetailsContext): CreateComputeInstanceOutputData | undefined {
  const outputs = context.execution.outputs as { default?: CreateComputeInstanceOutputPayload[] } | undefined;
  const payload = outputs?.default?.[0];
  return payload?.data;
}

export const createComputeInstanceMapper: ComponentBaseMapper = {
  props(context: ComponentBaseContext) {
    const props = baseMapper.props(context);
    return {
      ...props,
      metadata: createComputeInstanceMetadataList(context.node),
    };
  },

  subtitle(context: SubtitleContext) {
    return baseMapper.subtitle(context);
  },

  getExecutionDetails(context: ExecutionDetailsContext): Record<string, string> {
    const details: Record<string, string> = {};
    const data = getOutputData(context);

    const executedAt = getExecutedAt(context);
    if (executedAt) {
      details["Executed At"] = executedAt;
    }

    if (!data) return details;

    if (data.instanceId) {
      details["Instance ID"] = data.instanceId;
    }

    if (data.displayName) {
      details["Display Name"] = data.displayName;
    }

    if (data.lifecycleState) {
      details["State"] = data.lifecycleState;
    }

    if (data.shape) {
      details["Shape"] = data.shape;
    }

    if (data.availabilityDomain) {
      details["Availability Domain"] = data.availabilityDomain;
    }

    if (data.region) {
      details["Region"] = data.region;
    }

    if (data.publicIp) {
      details["Public IP"] = data.publicIp;
    }

    if (data.privateIp) {
      details["Private IP"] = data.privateIp;
    }

    return details;
  },
};

function createComputeInstanceMetadataList(node: ComponentBaseContext["node"]): MetadataItem[] {
  const config = node.configuration as CreateComputeInstanceConfiguration | undefined;
  const nodeMetadata = node.metadata as CreateComputeInstanceNodeMetadata | undefined;

  const displayName = nodeMetadata?.displayName ?? config?.displayName;
  const shape = nodeMetadata?.shape ?? config?.shape;
  const availabilityDomain = nodeMetadata?.availabilityDomain ?? config?.availabilityDomain;

  return [
    metadataItem("tag", displayName),
    metadataItem("cpu", shape),
    metadataItem("map-pin", availabilityDomain),
    metadataItem("disc", nodeMetadata?.imageName),
    metadataItem("network", nodeMetadata?.subnetName),
    metadataItem("database", nodeMetadata?.blockVolumeName),
  ]
    .filter(isMetadataItem)
    .slice(0, MAX_NODE_METADATA_ITEMS);
}
